import { PaymentPlanType } from './PaymentPlanType';
import { FeatureFlag, ProtectedEntityTypes } from '@nirvana/shared/constants';
import { CompanyType } from './company-type';
import { MdrServiceFlavor } from '../../license/types/MdrServiceFlavor';
import { ProductServiceStatus } from '../../license/types/ProductServiceStatus';
import { ProductServiceType } from '../../license/types/ProductServiceType';
import { LicenseType } from '../../license/types/LicenseType';
import { SubscriptionType } from '../../license/types/SubscriptionType';
import { DeepPartial } from 'typeorm';
import { LicenseProductStatus, LicenseUsage } from '../common/licensingGrid/types/licensing-grid-license';
import { LicenseProductIdEnum, PRODUCTS_WITH_MDR } from '@nirvana/shared/license-helper';
import { MspInProductTrialState } from '../common/mspInProductTrialsTab/types/msp-in-product-trials-types';
import { MdrContactImportSourceValue } from '../common/mdrContactDetails/common/types';
import { differenceInDays } from 'date-fns';
import { PhoneNumber } from '../../ui/gzPhoneInput/types';
import { isEmpty } from 'lodash';

export interface CompanyData {
  _id: string;
  company: null | ParentCompany;
  name: string;
  companyType: CompanyType;
  type: ProtectedEntityTypes.COMPANY; // CompanyData extends the ProtectedEntities model
  path: string;
  specifics: CompanySpecifics;
  subscription: SubscriptionType;
  readOnly?: boolean;
  children?: number;
  aioSvaIp?: string;
  visibilityTree?: string;
}

export interface ParentCompany {
  id: string;
  name: string;
  address: string;
  phone: string;
  hash: string;
  hasFratSubscription: boolean | null;
}

export interface CompanySpecifics {
  address: string;
  phone: string;
  industry?: number;
  mspInProductTrialStatus?: number;
  rights: CompanyRights;
  licenseAddons: LicenseAddon[];
  contactPerson?: ContactPerson;
  mdrContactInformation?: MdrContactInformation;
  isSuspended: boolean;
  issues: CompanyIssues;
  hash: string;
  paymentPlan: PaymentPlanType;
  cloudUss: CloudUss;
  useGoldenImageMode: boolean;
  disableIorUserMonitoring: boolean;
  canBeManagedByAbove: boolean;
  allowDownloadQuarantineByAbove: boolean;
  twoFactorAuthentication?: TwoFactorAuthentication;
  logoFileId: string;
  maEnforced: boolean;
  maEnabled: boolean;
  maToken: string;
  country: string;
  state: string;
  oemKitId: number;
  inheritedLicense: boolean;
  hasMspTrial: boolean;
  hasFratSubscription?: boolean | null;
  hasReservedSlots?: boolean;
  licenses?: License[];
  licenseUsageDetails?: LicenseUsage[];
  features: CompanyFeatures;
  targetedFeatures: CompanyTargetedFeatures;
  productServices: ProductService[];
  myAccount: MyAccount;
  hasBasicMonthlySubscription?: boolean;
  minimalCommitment?: MinimalCommitment;
  unlicensed: number;
  mspInProductTrials?: MspInProductTrials;
  isLinkedWithIntellizone?: boolean;
  superFastRingTestCompany?: boolean;
}

export interface CloudUss {
  provisionStatus: number;
  redacted: boolean;
}

export interface ContactPerson {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  companyRole?: string;
}

export interface MdrContactInformation {
  fullName?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  importedCompanyId?: string;
  importedAccountId?: string;
}

export interface MdrMandatoryContactInformation {
  fullName: string;
  email: string;
  phoneNumber?: PhoneNumber;
  hasVerifiedEmail: boolean;
  parent: string;
}

export type MdrContactInformationDbSchema = MdrContactImportSourceValue & {
  fullName?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
};

export interface CompanyIssues {
  hasCriticalIssues: boolean;
}

export interface License {
  key: string;
  type: number;
  used: number;
  expiryDate: Date;
  total?: number;
  reserved?: number;
  isAddon: boolean;
  productId: LicenseProductIdEnum;
  isMonthly?: boolean;
  endSubscription?: {
    endDate: string;
    autoRenewPeriod: number;
    inheritedFrom: string;
    endDateIsSet: boolean;
    autoRenewPeriodIsSet: boolean;
  };
}

export type CompanyFeatures = Partial<Record<FeatureFlag, boolean>>;

export interface MspInProductTrials {
  edr: MspInProductTrialState;
  mdr: MspInProductTrialState;
}

export interface CompanyTargetedFeatures {
  mdr?: MDR;
}

export interface MDR {
  remoteAccess?: boolean;
}

export interface ProductService {
  type: ProductServiceType;
  status: ProductServiceStatus;
  statusStartDate: Date;
  statusEndDate?: Date;
  activeSince?: Date;
  used: number;
  flavour: MdrServiceFlavor;
}

export interface LicenseAddon {
  key: string;
  productId: number;
  expiryDate: Date;
  status: LicenseProductStatus;
  created?: Date;
}

export interface MyAccount {
  enabled: boolean;
}

export interface CompanyRights {
  companyManager: boolean;
  manageCompanies: boolean;
  manageNetworks: boolean;
}

export interface TwoFactorAuthentication {
  enforce: boolean;
  allowance: number;
}

export interface MyCompanyAuthenticationData {
  type: number;
  specifics: CompanyAuthenticationSpecifics;
}

export interface CompanyAuthenticationSpecifics {
  twoFactorAuthentication?: TwoFactorAuthentication;
  enforcedPasswordLifetime: boolean; // this is stored as a number in mongo, but is transformed to bool when querying
  enforcePasswordLockdown?: boolean;
  identityProvider?: IdentityProvider;
}

export interface TwoFactorAuthentication {
  enforce: boolean;
  skip2FAPeriod: number;
}

export interface IdentityProvider {
  specifics: IdentityProviderSpecifics;
}

export interface IdentityProviderSpecifics {
  metadata: string;
}

export interface MinimalCommitment {
  usage: {
    endpoints: number;
    endpointsIsSet: boolean;
  };
}

export namespace CompanyData {
  export function getCompanyType(company: CompanyData): CompanyType {
    return company.specifics.rights.manageCompanies ? CompanyType.Partner : CompanyType.Customer;
  }

  export function getFirstBaseLicense(company: DeepPartial<CompanyData>) {
    return company.specifics?.licenses?.[0];
  }

  export function getLicenseType(company: DeepPartial<CompanyData>): LicenseType | undefined {
    return CompanyData.getFirstBaseLicense(company)?.type;
  }

  export function hasMonthlyLicense(company: DeepPartial<CompanyData>) {
    // companies with monthly have only one license
    return CompanyData.getFirstBaseLicense(company)?.type === LicenseType.Monthly;
  }

  export function getMonthlyLicense(company: DeepPartial<CompanyData>) {
    // companies with monthly have only one license
    return CompanyData.hasMonthlyLicense(company) ? CompanyData.getFirstBaseLicense(company) : undefined;
  }

  export function hasNonePaymentPlan(company: CompanyData) {
    return company.specifics.paymentPlan === PaymentPlanType.LICENSE_NONE;
  }

  export function hasYearlyPaymentPlan(company: CompanyData) {
    return company.specifics.paymentPlan === PaymentPlanType.YEARLY;
  }

  export function hasMonthlyPaymentPlan(company: CompanyData) {
    return company.specifics.paymentPlan === PaymentPlanType.MONTHLY;
  }

  export function hasCloudSecurityForMsp(company: CompanyData) {
    return this.hasMonthlyPaymentPlan(company) && !company.specifics.hasMspTrial && !company.specifics.inheritedLicense;
  }

  export function hasCloudSecurityForMspTrial(company: CompanyData) {
    return this.hasMonthlyPaymentPlan(company) && company.specifics.hasMspTrial && !company.specifics.inheritedLicense;
  }

  export function hasMonthlySubscription(company: CompanyData) {
    return company.specifics.inheritedLicense === true;
  }

  export function hasFratSubscription(company: CompanyData) {
    return !!company.specifics.hasFratSubscription;
  }

  export function doesParentCompanyHaveFratSubscription(company: CompanyData) {
    return !!company.company?.hasFratSubscription;
  }

  export function hasReservedSeatsForUnderneathCompanies(company: CompanyData) {
    const license = CompanyData.getMonthlyLicense(company);
    const hasReservedSeats = license?.reserved ?? 0;

    return hasReservedSeats > 0;
  }

  export function hasMdrLicense(company: CompanyData): boolean {
    return company.specifics.licenses?.some(license => PRODUCTS_WITH_MDR.includes(license.productId)) ?? false;
  }

  export function getMdrManagerStatus(company?: CompanyData): ProductServiceStatus | null {
    const productServices = company?.specifics.productServices ?? [];
    for (const productService of productServices) {
      if (productService.type === ProductServiceType.MDR_MANAGER) {
        return productService.status;
      }
    }
    return null;
  }

  export function isMdrContactFilled(company?: CompanyData): boolean {
    return !isEmpty(company?.specifics.mdrContactInformation);
  }

  export function shouldDisplayMdrStatus(company: CompanyData): boolean {
    if (hasMonthlyLicense(company) || !company?.specifics?.productServices) {
      return false;
    }

    const mdrProductService = company?.specifics.productServices.find(
      (service: any) => service.type === ProductServiceType.MDR
    );

    if (!mdrProductService) {
      return false;
    }

    if (mdrProductService.status !== ProductServiceStatus.INACTIVE) {
      return true;
    }

    // if less than 30 days since inactive, display the section
    const statusStart = new Date(mdrProductService.statusStartDate);
    const currentDate = new Date();
    const daysInactive = differenceInDays(currentDate, statusStart);

    return daysInactive < 30;
  }

  export function hasProductServiceWithStatus(
    company: CompanyData,
    serviceType: ProductServiceType,
    statuses: ProductServiceStatus[]
  ): boolean {
    const productServices = company?.specifics.productServices ?? [];
    for (const productService of productServices) {
      if (productService.type === serviceType) {
        return statuses.includes(productService.status);
      }
    }
    return false;
  }

  export function determineCompanySubscription(company: CompanyData): SubscriptionType {
    const licenseType = CompanyData.getLicenseType(company);

    if (licenseType === LicenseType.Trial) {
      return SubscriptionType.Trial;
    } else if (
      licenseType === LicenseType.Monthly &&
      company.specifics.inheritedLicense &&
      company.specifics.hasMspTrial
    ) {
      // inherited monthly trial
      return SubscriptionType.InheritedMspTrial;
    } else if (licenseType === LicenseType.Monthly && company.specifics.hasBasicMonthlySubscription) {
      // basic monthly subscription (this is inherited, so must be checked before inherited monthly)
      return SubscriptionType.BasicMonthly;
    } else if (licenseType === LicenseType.Monthly && company.specifics.inheritedLicense) {
      // inherited monthly
      return SubscriptionType.Inherited;
    } else if (licenseType === LicenseType.Monthly && company.specifics.hasMspTrial) {
      // Monthly License Trial
      return SubscriptionType.MonthlyLicenseTrial;
    } else if (licenseType === LicenseType.EndUser && company.specifics.hasFratSubscription) {
      // frat subscription
      return SubscriptionType.Frat;
    }

    // company has no license or has direct monthly or normal enduser license
    // switch default subscription to Licensed
    return SubscriptionType.Licensed;
  }
}
